import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import type { FilterItemsEmailT } from '@/types/filterItems';

import FilterItemEmail from './FilterItemEmail';
import FilterItemsAddEmail from './FilterItemsAddEmail';
import { DiffType } from './type';

export type UpdatedFilterItemT = {
  diffType: DiffType;
  value: string;
  type: 'email' | 'domain';
};

type FilterItemsEmailProps = {
  data?: FilterItemsEmailT;
  onSaveChange: ({
    addedEmails,
    deletedEmails,
    addedEmailDomains,
    deletedEmailDomains,
  }: {
    addedEmails: string[];
    deletedEmails: string[];
    addedEmailDomains: string[];
    deletedEmailDomains: string[];
  }) => Promise<FilterItemsEmailT>;
  setHasEmailTabChange: Dispatch<SetStateAction<boolean>>;
  editDisabled?: boolean;
};

const FilterItemsEmail = (props: FilterItemsEmailProps) => {
  const color = useColor();
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));

  const [existingEmails, setExistingEmails] = useState<string[]>(
    props.data?.emails || [],
  );
  const [existingEmailDomains, setExistingEmailDomains] = useState<string[]>(
    props.data?.email_domains || [],
  );
  const [updatedEmailFilters, setUpdatedEmailFilters] = useState<
    UpdatedFilterItemT[]
  >([]);

  const { setPopup } = usePopupStore((state) => ({
    setPopup: state.setPopup,
  }));

  const hasChange = updatedEmailFilters.length > 0;

  useEffect(() => {
    props.setHasEmailTabChange(hasChange);
  }, [hasChange]);

  const handleConfirmAddEmailInfo = (
    type: 'email' | 'domain',
    value: string,
  ) => {
    setUpdatedEmailFilters((prev) => [
      ...prev,
      { diffType: DiffType.ADDED, value, type },
    ]);
  };

  const handleDeleteExistingEmail = (value: string) => {
    setExistingEmails((prev) => prev.filter((email) => email !== value));
    setUpdatedEmailFilters((prev) => [
      ...prev,
      { diffType: DiffType.DELETED, value, type: 'email' },
    ]);
  };

  const handleDeleteExistingEmailDomain = (value: string) => {
    setExistingEmailDomains((prev) => prev.filter((email) => email !== value));
    setUpdatedEmailFilters((prev) => [
      ...prev,
      { diffType: DiffType.DELETED, value, type: 'domain' },
    ]);
  };

  const handleDeleteUpdatedEmailFilter = (index: number) => {
    const deletedEmailFilter = updatedEmailFilters[index];

    if (deletedEmailFilter.diffType === DiffType.DELETED) {
      if (deletedEmailFilter.type === 'email') {
        setExistingEmails((prev) => [...prev, deletedEmailFilter.value]);
      } else {
        setExistingEmailDomains((prev) => [...prev, deletedEmailFilter.value]);
      }
    }

    setUpdatedEmailFilters((prev) => prev.filter((_, i) => i !== index));
  };

  const saveChange = async () => {
    if (!hasChange) {
      return;
    }

    const uniqueOldEmailSet = [...new Set(props.data?.emails)];
    const uniqueOldEmailDomainSet = [...new Set(props.data?.email_domains)];

    const uniqueAddedEmailSet = [
      ...new Set(
        updatedEmailFilters
          .filter(
            (item) => item.diffType === DiffType.ADDED && item.type === 'email',
          )
          .map((item) => item.value),
      ),
    ];

    const uniqueDeletedEmailSet = [
      ...new Set(
        updatedEmailFilters
          .filter(
            (item) =>
              item.diffType === DiffType.DELETED && item.type === 'email',
          )
          .map((item) => item.value),
      ),
    ];

    const uniqueAddedEmailDomains = [
      ...new Set(
        updatedEmailFilters
          .filter(
            (item) =>
              item.diffType === DiffType.ADDED && item.type === 'domain',
          )
          .map((item) => item.value),
      ),
    ];

    const uniqueDeletedEmailDomains = [
      ...new Set(
        updatedEmailFilters
          .filter(
            (item) =>
              item.diffType === DiffType.DELETED && item.type === 'domain',
          )
          .map((item) => item.value),
      ),
    ];

    const addedEmails = uniqueAddedEmailSet.filter(
      (email) => !uniqueOldEmailSet.includes(email),
    );
    const deletedEmails = uniqueDeletedEmailSet.filter((email) =>
      uniqueOldEmailSet.includes(email),
    );
    const addedEmailDomains = uniqueAddedEmailDomains.filter(
      (emailDomain) => !uniqueOldEmailDomainSet.includes(emailDomain),
    );
    const deletedEmailDomains = uniqueDeletedEmailDomains.filter(
      (emailDomain) => uniqueOldEmailDomainSet.includes(emailDomain),
    );

    try {
      const data = await props.onSaveChange({
        addedEmails,
        deletedEmails,
        addedEmailDomains,
        deletedEmailDomains,
      });

      if (data) {
        setExistingEmails(data.emails || []);
        setExistingEmailDomains(data.email_domains || []);
        setUpdatedEmailFilters([]);
      }
    } catch (e) {
      addDefaultToast('Failed to save changes', 'fail');
      console.error(e);
    }
  };

  const handleClickSaveChange = async () => {
    if (!hasChange) {
      return;
    }

    if (props.editDisabled) {
      return;
    }

    setPopup({
      popupTheme: 'neutral',
      type: 'confirm',
      title: 'Are you sure you want to save the changes?',
      message:
        'It may take a few minutes to reflect the changes. Then, all your data will be re-analyzed using the list.',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: saveChange,
    });
  };

  return (
    <>
      <Space h={36} />

      <Flex j="space-between" a="center">
        <Typo type="label2" weight="sb" className="text-gray-800">
          Add email
        </Typo>
        <Button
          disabled={props.editDisabled || !hasChange}
          sizeType="m"
          variant="primary"
          onClick={handleClickSaveChange}
        >
          Save updates
        </Button>
      </Flex>

      <Space h={16} />

      <FilterItemsAddEmail
        onConfirm={handleConfirmAddEmailInfo}
        existingEmails={existingEmails.concat(
          updatedEmailFilters
            .filter((item) => item.type === 'email')
            .map((item) => item.value),
        )}
        existingEmailDomains={existingEmailDomains.concat(
          updatedEmailFilters
            .filter((item) => item.type === 'domain')
            .map((item) => item.value),
        )}
        editDisabled={props.editDisabled}
      />

      <Space h={40} />

      <Typo type="label2" weight="sb" className="text-gray-800">
        Pending List
      </Typo>
      <List
        data={updatedEmailFilters}
        className="gap-2 py-6"
        render={(emailFilter, index) => {
          return (
            <Flex key={index} a="center" g={12}>
              {emailFilter.diffType === DiffType.ADDED ? (
                <PlusCircleOutlined
                  style={{ color: color.green600, fontSize: '16px' }}
                />
              ) : (
                <MinusCircleOutlined
                  style={{ color: color.red600, fontSize: '16px' }}
                />
              )}

              <FilterItemEmail
                readOnly
                key={index}
                text={emailFilter.value}
                actionButton={
                  <IconButton
                    className="delete-button"
                    icon={
                      emailFilter.diffType === DiffType.ADDED ? (
                        <DeleteOutlined />
                      ) : (
                        <UndoOutlined />
                      )
                    }
                    outlined={false}
                    onClick={() => {
                      handleDeleteUpdatedEmailFilter(index);
                    }}
                    sizeType="l"
                    style={{
                      flexShrink: 0,
                    }}
                  />
                }
              />
            </Flex>
          );
        }}
      />

      <Space h={16} />

      <Typo type="label2" weight="sb" className="text-gray-800">
        Existing List
      </Typo>
      <List
        data={existingEmails
          .map((email) => ({ type: 'email', email }))
          .concat(
            existingEmailDomains.map((email) => ({ type: 'domain', email })),
          )}
        className="gap-2 py-6 overflow-unset"
        render={(email, index) => {
          return (
            <FilterItemEmail
              key={index + email.email}
              readOnly
              text={email.email}
              actionButton={
                !props.editDisabled && (
                  <IconButton
                    className="delete-button"
                    icon={<DeleteOutlined />}
                    outlined={false}
                    onClick={() => {
                      if (email.type === 'email') {
                        handleDeleteExistingEmail(email.email);
                      } else if (email.type === 'domain') {
                        handleDeleteExistingEmailDomain(email.email);
                      }
                    }}
                    sizeType="l"
                    style={{
                      flexShrink: 0,
                    }}
                  />
                )
              }
            />
          );
        }}
      />
    </>
  );
};

export default FilterItemsEmail;
